import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { DictionaryActions } from "../actions";
import { filter, first, map, switchMap, tap } from "rxjs";
import { DictionaryService } from "../../services/dictionary.service";
import { DictionaryModel } from "../../interfaces/dictionary-model";
import { BUTTON_TEXT, GLOBAL_MESSAGES } from "../../variables/strings";
import { MatSnackBar } from "@angular/material/snack-bar";

@Injectable({
    providedIn: "root",
})
export class DictionaryEffects {
    private readonly actions$ = inject(Actions);

    private readonly dictionaryService = inject(DictionaryService);

    private readonly snackBar = inject(MatSnackBar);

    readonly getDictionary$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(DictionaryActions.getDictionary),
            switchMap(() =>
                this.dictionaryService
                    .getDictionary()
                    .pipe(filter((response) => !!response)),
            ),
            map((dictionary) =>
                DictionaryActions.setDictionary({ dictionary }),
            ),
        );
    });

    readonly getValueTypes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(DictionaryActions.initValueTypesDictionary),
            first(),
            switchMap(() => this.dictionaryService.getValueTypes()),
            map((valueTypes) =>
                DictionaryActions.setValueTypesDictionary({ valueTypes }),
            ),
        );
    });

    readonly getNodeAttributes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(DictionaryActions.getNodeAttributes),
            filter((publicationId) => !!publicationId),
            switchMap(({ publicationId }) =>
                this.dictionaryService
                    .getNodeAttributes(publicationId!)
                    .pipe(
                        map((data) => [
                            data.section as DictionaryModel[],
                            data.resource as DictionaryModel[],
                        ]),
                    ),
            ),
            map((nodeAttributes) =>
                DictionaryActions.setNodeAttributes({ nodeAttributes }),
            ),
        );
    });

    readonly clearNodeAttributes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(DictionaryActions.clearNodeAttributes),
            map(() => [[], []]),
            map((nodeAttributes) =>
                DictionaryActions.setNodeAttributes({ nodeAttributes }),
            ),
        );
    });

    readonly setSectionsParams$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(DictionaryActions.setNodeAttributes),
            map(({ nodeAttributes }) => {
                const [sectionsParams] = nodeAttributes;
                return sectionsParams;
            }),
            map((sectionsParams) =>
                DictionaryActions.setSectionsParams({ sectionsParams }),
            ),
        );
    });

    readonly setResourceParams$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(DictionaryActions.setNodeAttributes),
            map(({ nodeAttributes }) => {
                const [, resourceParams] = nodeAttributes;
                return resourceParams;
            }),
            map((resourceParams) =>
                DictionaryActions.setResourceParams({ resourceParams }),
            ),
        );
    });

    readonly addParamToDictionary$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(DictionaryActions.addParamToDictionary),
            switchMap(({ parametr }) =>
                this.dictionaryService.addParamToDictionary(parametr),
            ),
            tap(() =>
                this.snackBar.open(GLOBAL_MESSAGES.SAVED, BUTTON_TEXT.CLOSE, {
                    duration: 5000,
                    panelClass: "save-snackbar",
                    verticalPosition: "bottom",
                    horizontalPosition: "end",
                }),
            ),
            map(() => DictionaryActions.getDictionary()),
        );
    });
}
